import React, { useState } from "react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"

interface CreateCampaignDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (data: { name: string; completionCriteria: string }) => Promise<void>
}

export function CreateCampaignDialog({
  open,
  onOpenChange,
  onConfirm,
}: CreateCampaignDialogProps) {
  const [name, setName] = useState("")
  const [completionCriteria, setCompletionCriteria] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (!name.trim() || !completionCriteria.trim()) return
    setSubmitting(true)
    try {
      await onConfirm({ name: name.trim(), completionCriteria: completionCriteria.trim() })
      setName("")
      setCompletionCriteria("")
      onOpenChange(false)
    } catch {
      // error handled by parent
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Create Campaign</DialogTitle>
          <DialogDescription>
            Create a new patient engagement campaign. It will be created as active.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="campaign-name">Name</Label>
            <Input
              id="campaign-name"
              placeholder="e.g. Diabetes A1c Follow-up"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="completion-criteria">Completion Criteria</Label>
            <Textarea
              id="completion-criteria"
              placeholder="e.g. Patient completes a check-in call and confirms medication adherence"
              value={completionCriteria}
              onChange={(e) => setCompletionCriteria(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || !completionCriteria.trim() || submitting}
            className="w-full sm:w-auto"
          >
            {submitting ? "Creating..." : "Create Campaign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
